import { stripParenthesesContent } from 'components/shared/processes/strings/stripContent'
import {
	formatTreatmentDuration,
	getTreatmentSchemaName,
} from 'components/cattles/treatmentPage/utils/treatmentSchemaDisplay'
import {
	ICheckupList,
	IDiagnosisBlock,
	IExaminersTrim,
	PositionHoof,
} from 'models'
import { ITreatmentSchema } from 'models/ICattlesData'
import { hoofLabels } from 'ui/HoofPicker/helpers/getLabel'

export type CheckupHistoryEvent = {
	label: string
	isError?: boolean
}

export type DiagnosisHoofCell = {
	diagnosis: string
	hooves: string
}

export type HistorySchemeCell = {
	title: string
	durationLabel: string
}

const HOOF_ORDER = [
	PositionHoof.Front_Left,
	PositionHoof.Front_Right,
	PositionHoof.Rear_Left,
	PositionHoof.Rear_Right,
]

export const formatHoofList = (positions: PositionHoof[]) => {
	const unique = HOOF_ORDER.filter(position => positions.includes(position))
	return unique
		.map((position, index) => {
			const label = hoofLabels[position] || position
			return index === 0 ? label : label.toLowerCase()
		})
		.join(', ')
}

export const EMPTY_HISTORY_CELL = '-'

const hasTreatmentDiagnosis = (diagnosis: IDiagnosisBlock[] | undefined) =>
	(diagnosis || []).some(
		item =>
			(item.sickness && item.sickness.length > 0) ||
			(item.sickness_id && item.sickness_id.length > 0) ||
			(item.treatment_plans && item.treatment_plans.length > 0) ||
			(item.treatment_plans_id && item.treatment_plans_id.length > 0),
	)

const hasTrimEvent = (trim?: IExaminersTrim) =>
	Boolean(trim?.isTrim || trim?.done_dt)

export const getCheckupHistoryEvents = (
	exam: Pick<ICheckupList, 'diagnosis' | 'dispose_recommended'> & {
		trim?: IExaminersTrim
	},
): CheckupHistoryEvent[] => {
	const hasTreatment = hasTreatmentDiagnosis(exam.diagnosis)
	const hasTrim = hasTrimEvent(exam.trim)
	const events: CheckupHistoryEvent[] = []

	if (!hasTreatment && !hasTrim && !exam.dispose_recommended) {
		events.push({ label: 'Хромота' })
	}
	if (hasTreatment) events.push({ label: 'Лечение' })
	if (hasTrim) events.push({ label: 'Обрезка' })
	if (exam.dispose_recommended) {
		events.push({ label: 'Рекомендация на выбытие', isError: true })
	}

	return events.length ? events : [{ label: 'Хромота' }]
}

export type CheckupHistoryRow = {
	id: string
	createdDt?: number
	limpness?: number
	event: CheckupHistoryEvent
	diagnosisHoof: DiagnosisHoofCell[]
	schemes: HistorySchemeCell[]
	performer: string
}

export const buildCheckupHistoryRows = (
	exams: ICheckupList[] = [],
	schemas: ITreatmentSchema[] = [],
): CheckupHistoryRow[] =>
	exams.flatMap((exam, index) => {
		const performer = formatExaminerName(exam.examiner)
		const diagnosisHoof = mergeDiagnosisWithHoof(exam.diagnosis)
		const schemes = getHistorySchemes(exam.diagnosis, schemas)
		const hasTreatment = hasTreatmentDiagnosis(exam.diagnosis)
		const hasTrim = hasTrimEvent(exam.trim)
		const examKey = exam.id ?? index
		const base = {
			createdDt: exam.created_dt,
			limpness: exam.limpness,
			performer,
		}
		const rows: CheckupHistoryRow[] = []

		if (hasTreatment) {
			rows.push({
				...base,
				id: `${examKey}-treatment`,
				event: { label: 'Лечение' },
				diagnosisHoof,
				schemes,
			})
		}
		if (hasTrim) {
			rows.push({
				...base,
				id: `${examKey}-trim`,
				event: { label: 'Обрезка' },
				diagnosisHoof: [],
				schemes: [],
			})
		}
		if (exam.dispose_recommended) {
			rows.push({
				...base,
				id: `${examKey}-dispose`,
				event: { label: 'Рекомендация на выбытие', isError: true },
				diagnosisHoof: [],
				schemes: [],
			})
		}
		if (!rows.length) {
			rows.push({
				...base,
				id: `${examKey}-lameness`,
				event: { label: 'Хромота' },
				diagnosisHoof: [],
				schemes: [],
			})
		}

		return rows
	})

export const mergeDiagnosisWithHoof = (
	diagnosis: IDiagnosisBlock[] = [],
): DiagnosisHoofCell[] => {
	const groups = new Map<string, PositionHoof[]>()

	diagnosis.forEach(item => {
		const titles = (item.sickness || [])
			.map(sickness => stripParenthesesContent(sickness?.title || '').trim())
			.filter(Boolean)
			.join(', ')
		if (!titles) return

		const positions = groups.get(titles) || []
		if (!positions.includes(item.position)) positions.push(item.position)
		groups.set(titles, positions)
	})

	return Array.from(groups.entries()).map(([title, positions]) => ({
		diagnosis: title,
		hooves: formatHoofList(positions),
	}))
}

export const getHistorySchemes = (
	diagnosis: IDiagnosisBlock[] = [],
	schemas: ITreatmentSchema[] = [],
): HistorySchemeCell[] => {
	const seen = new Set<number>()
	const result: HistorySchemeCell[] = []

	const pushScheme = (
		id: number,
		fallbackTitle?: string,
		fallbackDuration?: number,
	) => {
		if (!id || seen.has(id)) return
		seen.add(id)
		const schema = schemas.find(item => item.id === id)
		const title =
			fallbackTitle || (schema ? getTreatmentSchemaName(schema) : '') || ''
		if (!title) return
		const duration = schema?.duration ?? fallbackDuration
		result.push({
			title,
			durationLabel:
				duration === null || duration === undefined
					? ''
					: `Продолжительность: ${formatTreatmentDuration(duration)}`,
		})
	}

	diagnosis.forEach(item => {
		;(item.treatment_plans || []).forEach(plan =>
			pushScheme(plan.id, plan.title || plan.description),
		)
		;(item.treatment_plans_id || []).forEach(id => pushScheme(id))
	})

	return result
}

export const formatExaminerName = (
	examiner?: {
		firstname?: string
		lastname?: string
	} | null,
) => {
	if (!examiner) return ''
	const lastName = examiner.lastname?.trim() || ''
	const initial = examiner.firstname?.trim()?.[0]
	if (lastName && initial) return `${lastName} ${initial}.`
	return lastName || examiner.firstname?.trim() || ''
}

export const sortByGroupTitle = <T extends { group?: string; title: string }>(
	items: T[] = [],
) =>
	[...items].sort(
		(a, b) =>
			(a.group || '').localeCompare(b.group || '', 'ru') ||
			a.title.localeCompare(b.title, 'ru'),
	)

const isHealthyLabel = (value?: string) =>
	(value ?? '').trim().toLowerCase() === 'здорова'

export const excludeHealthyDiagnoses = <T extends { title: string }>(
	items: T[] = [],
) => items.filter(item => !isHealthyLabel(item.title))
